import React from 'react';
import { AnimatePresence } from 'motion/react';

import { Button } from '@oysterjs/ui/button';

import { SubscriptionEntitlement, type CustomPresetFieldsFragment } from '~/api/graphql';
import { useEntitlements } from '~/components/entitlements';
import {
  AVAILABLE_USE_CASES,
  useCaseById,
  type UseCaseSpec
} from '~/components/home/use-case-catalog';
import { UseCaseBrowser } from '~/components/home/use-case-browser';
import { CustomPresetChip, UseCaseChip } from '~/components/home/use-case-tile';
import { type TriggerTask } from './trigger-form';
import { builtInTaskTitle, PRESET_UNAVAILABLE } from './trigger-utils';

/**
 * A use case that needs a library template is left out: a trigger has nowhere
 * to say which template, so the run would start without it.
 */
const TRIGGER_USE_CASES = AVAILABLE_USE_CASES.filter((useCase) => !useCase.requiresLibrary);

const LINK_CLASS =
  'font-medium text-primary-600 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300';

/** The use case behind a built-in task, when the catalog still lists it. */
export const taskUseCase = (task: TriggerTask | null): UseCaseSpec | undefined =>
  task?.kind === 'builtIn' && task.useCase ? useCaseById(task.useCase) : undefined;

/** The chosen preset with Change and Remove links, or the button that opens the preset browser. */
export const TaskSelection = (props: {
  chip?: React.ReactNode;
  title: string | null;
  disabled?: boolean;
  onChoose: () => void;
  onRemove: () => void;
}) =>
  props.title !== null ? (
    <div data-slot="control" className="flex items-start gap-3">
      {props.chip}
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-sm font-medium text-neutral-900 dark:text-white">
          {props.title}
        </span>
        <span className="flex gap-3 text-xs">
          <button
            type="button"
            disabled={props.disabled}
            onClick={props.onChoose}
            className={LINK_CLASS}
          >
            Change
          </button>
          <button
            type="button"
            disabled={props.disabled}
            onClick={props.onRemove}
            className={LINK_CLASS}
          >
            Remove
          </button>
        </span>
      </div>
    </div>
  ) : (
    <Button
      data-slot="control"
      color="white"
      type="button"
      disabled={props.disabled}
      onClick={props.onChoose}
    >
      Choose a preset
    </Button>
  );

export const TriggerTaskField = (props: {
  value: TriggerTask | null;
  customPresets: CustomPresetFieldsFragment[];
  disabled?: boolean;
  onChange: (task: TriggerTask | null) => void;
}) => {
  const [browsing, setBrowsing] = React.useState(false);
  const { hasEntitlement } = useEntitlements();
  const { value } = props;
  const useCase = taskUseCase(value);

  const selected =
    value?.kind === 'builtIn'
      ? {
          chip: useCase && <UseCaseChip useCase={useCase} />,
          title: builtInTaskTitle(value)
        }
      : value?.kind === 'custom'
        ? {
            chip: <CustomPresetChip />,
            title:
              props.customPresets.find((preset) => preset.id === value.presetId)?.title ??
              PRESET_UNAVAILABLE
          }
        : null;

  return (
    <>
      <TaskSelection
        chip={selected?.chip}
        title={selected?.title ?? null}
        disabled={props.disabled}
        onChoose={() => setBrowsing(true)}
        onRemove={() => props.onChange(null)}
      />

      <AnimatePresence mode="wait" initial>
        {browsing && !props.disabled && (
          <UseCaseBrowser
            key="trigger-preset-browser"
            title="Choose a preset"
            useCases={TRIGGER_USE_CASES}
            customPresets={props.customPresets}
            hasProAccess={hasEntitlement(SubscriptionEntitlement.CopilotProPresets)}
            onClose={() => setBrowsing(false)}
            onRun={(id) => {
              const spec = useCaseById(id);
              if (spec) {
                props.onChange({ kind: 'builtIn', preset: spec.preset, useCase: id });
              }
              setBrowsing(false);
            }}
            onRunCustomPreset={(presetId) => {
              props.onChange({ kind: 'custom', presetId });
              setBrowsing(false);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
};
